import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaArrowRight, FaCopy, FaRegCheckCircle, FaLaptopCode, FaCheck,
  FaServer, FaNetworkWired, FaTools, FaCloud, FaChartLine
} from 'react-icons/fa';
import { useTheme } from '../ThemeContext';

export default function DevOpsDetail() {
  useTheme();

  // Cloud Archetypes Configuration
  const archetypes = {
    serverless: {
      name: 'Serverless Edge API (AWS Lambda)',
      desc: 'On-demand stateless execution scaling instantly with zero idle server cost.',
      baseNodes: 0,
      prefix: 'lambda_router'
    },
    eks: {
      name: 'Managed Container Cluster (EKS)',
      desc: 'Dockerized microservices managed on isolated AWS EC2 host groups.',
      baseNodes: 2,
      prefix: 'eks_nodegroup'
    },
    cdn: {
      name: 'Global Edge Router (CloudFront)',
      desc: 'Reverse proxy and CDNs cached file distributions on global edge nodes.',
      baseNodes: 1,
      prefix: 'cloudfront_cdn'
    }
  };

  const [activeArch, setActiveArch] = useState('eks');
  const [cpuThreshold, setCpuThreshold] = useState(70); // CPU trigger percentage path
  const [trafficLoad, setTrafficLoad] = useState('idle'); // idle, spike, surge
  const [activeNodes, setActiveNodes] = useState(['node-01', 'node-02']);
  const [copiedSpec, setCopiedSpec] = useState(false);

  // Dynamic Metrics states
  const [currentCpu, setCurrentCpu] = useState(12);
  const [currentReqs, setCurrentReqs] = useState(12);
  const [latency, setLatency] = useState(18);

  // Logs Output
  const [autoscalerLogs, setAutoscalerLogs] = useState([
    'SYSTEM: Network load-balancer outpost provisioned. Monitors active.'
  ]);

  const addLog = (msg) => {
    setAutoscalerLogs(prev => [msg, ...prev.slice(0, 7)]);
  };

  // Sync archetype changes
  useEffect(() => {
    const arch = archetypes[activeArch];
    const initialNodes = Array.from({ length: arch.baseNodes }, (_, i) => `node-${String(i + 1).padStart(2, '0')}`);
    setActiveNodes(initialNodes);
    setTrafficLoad('idle');
    setCurrentCpu(12);
    setCurrentReqs(12);
    setLatency(18);
    addLog(`SYSTEM: Switched cluster architecture ➜ ${arch.name}`);
  }, [activeArch]);

  // Handle traffic load changes
  const applyTrafficLoad = (load) => {
    setTrafficLoad(load);
    
    if (load === 'idle') {
      setCurrentCpu(14);
      setCurrentReqs(18);
      setLatency(12);
      
      const arch = archetypes[activeArch];
      const baseline = Array.from({ length: arch.baseNodes || 1 }, (_, i) => `node-${String(i + 1).padStart(2, '0')}`);
      setActiveNodes(baseline);
      addLog(`[traffic.monitor] Load stabilized at idle ➜ 18 reqs/sec`);
    } 
    
    else if (load === 'spike') {
      setCurrentCpu(54);
      setCurrentReqs(840);
      setLatency(28);
      
      const arch = archetypes[activeArch];
      const baseline = Array.from({ length: Math.max(arch.baseNodes + 1, 3) }, (_, i) => `node-${String(i + 1).padStart(2, '0')}`);
      setActiveNodes(baseline);
      addLog(`[traffic.monitor] Traffic Spike detected! ➜ 840 reqs/sec`);
      addLog(`[autoscaler.scale] CPU utilization sits at 54%. Spawning active route nodes.`);
    } 
    
    else if (load === 'surge') {
      setCurrentCpu(92);
      setCurrentReqs(5800);
      setLatency(165);
      
      addLog(`[traffic.monitor] CRITICAL WARNING: Inbound Surge ➜ 5,800 reqs/sec!`);
      
      // Delay autoscaler scale out simulation
      setTimeout(() => {
        const arch = archetypes[activeArch];
        const scaled = Array.from({ length: Math.max(arch.baseNodes + 3, 5) }, (_, i) => `node-${String(i + 1).padStart(2, '0')}`);
        setActiveNodes(scaled);
        setCurrentCpu(cpuThreshold - 8); // Scaled out stabilizes load
        setLatency(38);
        addLog(`[autoscaler.scale] CPU util [92%] breached threshold [${cpuThreshold}%]`);
        addLog(`[autoscaler.scale] Provisioned +3 nodes infrastructure. Load stabilized.`);
      }, 900);
    }
  };

  const handleCopySpec = () => {
    const specSummary = `BCC DevOps Config:
- Architecture: ${archetypes[activeArch].name}
- CPU Trigger point: ${cpuThreshold}%
- Target nodes active under peak: ${activeNodes.length} worker nodes`;
    navigator.clipboard.writeText(specSummary);
    setCopiedSpec(true);
  };

  useEffect(() => {
    if (copiedSpec) {
      const timer = setTimeout(() => setCopiedSpec(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedSpec]);

  // Automated Timeline milestone indices
  const [activeStage, setActiveStage] = useState('terraform');

  return (
    <div className="min-h-screen overflow-x-hidden transition-all duration-300" style={{ background: 'var(--bg-primary)' }}>
      
      {/* ── HERO SECTION ── */}
      <section className="relative pt-32 pb-24 px-6 overflow-hidden animate-fade-in" 
               style={{ 
                 background: 'var(--bg-card)',
                 borderBottom: '1px solid var(--section-divider)' 
               }}>
        <div className="absolute top-0 right-0 w-[550px] h-[550px] rounded-full blur-[140px] opacity-10 pointer-events-none bg-cyan-500" />
        
        <div className="section-container relative z-10 text-center max-w-4xl">
          
          {/* Cloud category Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border border-cyan-505/20 bg-cyan-500/10 shadow-sm"
               style={{ borderColor: 'var(--border-color)' }}>
            <span className="w-2.5 h-2.5 rounded bg-clip-border flex items-center justify-center shrink-0">
               <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-ping" />
            </span>
            <span className="text-xs font-black uppercase tracking-wider text-cyan-400">High-Availability Solutions</span>
          </div>

          <h1 className="display-lg mb-6 leading-tight font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>
            DevOps & <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-blue-400">Cloud Scaling</span>.
          </h1>
          
          <p className="text-lg leading-relaxed mb-10 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            We provision multi-region Kubernetes worker clusters, automated Terraform infrastructure, zero-loss CI/CD build paths, and autoscale thresholds designed to hold back spike events.
          </p>

          <div className="flex justify-center gap-4">
            <Link to="/contactus" className="btn-primary inline-flex bg-gradient-to-r from-cyan-500 to-blue-550 text-white border-none hover:opacity-90">
              Build DevOps Topology <FaArrowRight size={11} className="ml-1.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TECHNICAL CAPABILITIES ── */}
      <section className="py-24 px-6 border-b animate-fade-in" style={{ background: 'var(--bg-primary)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-6xl">
          
          <div className="text-center mb-20">
            <p className="eyebrow mb-2">Capabilities Deck</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Cloud Infrastructure</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Our engineering systems are designed to automate deployment scripts, secure network subnets, and scale nodes dynamically.
            </p>
          </div>

          <div className="space-y-28">

            {/* Row 1: Infrastructure as Code */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="p-8 rounded-3xl border bg-gradient-to-br from-cyan-550/10 to-blue-500/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center text-cyan-400 mb-6">
                  <FaLaptopCode size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Infrastructure as Code (Terraform)</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Secure stateful cloud resource mapping. We write structured, module-based Terraform files to deploy isolated VPC subnet gates, database clusters, and routing configurations.
                </p>
              </div>
              <div>
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-cyan-400 mb-4">
                  <span className="w-4 h-px bg-cyan-400 inline-block" /> Automated Provisioning
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Terraform Infrastructure states
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  We template VPC layouts, IAM credential hierarchies, and cloud policies so environments deploy predictably without human error.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-cyan-500 mt-1 shrink-0" />
                    Multi-environment states synchronization (Staging & Production).
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-cyan-500 mt-1 shrink-0" />
                    Secure database replication and automatic snapshot tasks.
                  </li>
                </ul>
              </div>
            </div>

            {/* Row 2: Secure Load-Balancer */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <span className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-blue-400 mb-4">
                  <span className="w-4 h-px bg-blue-400 inline-block" /> Auto-Scaling Systems
                </span>
                <h3 className="text-3xl font-black mb-4 leading-tight" style={{ color: 'var(--text-primary)' }}>
                  Multi-Region Deployments
                </h3>
                <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
                  Mitigate geographical lag. We setup global CloudFront edge delivery, database replication mappings, and ALB routing protocols to manage traffic loads locally.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-blue-500 mt-1 shrink-0" />
                    Sub-second load-balanced target health checks.
                  </li>
                  <li className="flex items-start gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                    <FaCheck size={10} className="text-blue-500 mt-1 shrink-0" />
                    Isolated CDN edge caching rules for assets libraries.
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2 p-8 rounded-3xl border bg-gradient-to-br from-blue-500/10 to-cyan-550/5 shadow-inner" style={{ borderColor: 'var(--border-color)' }}>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-405 mb-6">
                  <FaServer size={20} />
                </div>
                <h3 className="text-lg font-black mb-3 text-white">Global Routing Matrices</h3>
                <p className="text-sm leading-relaxed text-zinc-400">
                  Ensure 99.99% uptime. Define active-active failover mechanisms, automatic failback triggers, and DNS maps routing filters.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── THE INTERACTIVE LOAD BUILDER ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-surface)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-5xl">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Scaling simulator</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Interactive High-Availability Mockup</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Choose a cloud architecture configuration, set CPU scale thresholds, and trigger traffic loads to watch auto-scaler tasks execute.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
            
            {/* Left Controls (5 columns) */}
            <div className="lg:col-span-5 flex flex-col justify-between border rounded-3xl p-6 shadow-xl"
                 style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
              <div>
                
                {/* Selector */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-3 text-cyan-400">1. Target Architecture Topology</h3>
                <div className="space-y-2 mb-6">
                  {Object.keys(archetypes).map(key => (
                    <button
                      key={key}
                      onClick={() => setActiveArch(key)}
                      className={`w-full text-left p-3 rounded-xl border flex items-center gap-3 transition-all ${
                        activeArch === key 
                          ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-400' 
                          : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                      }`}
                    >
                      <div className="w-2.5 h-2.5 rounded-full bg-cyan-455 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="text-[10px] font-black uppercase text-white leading-none">{archetypes[key].name}</h4>
                        <p className="text-[8.5px] text-zinc-550 truncate mt-1">{archetypes[key].desc}</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Scaling parameters */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-2.5 text-cyan-400">2. Configure Scaling Threshold</h3>
                <div className="space-y-4 mb-6">
                  
                  {/* CPU Trigger Slider */}
                  <div className="p-3 bg-zinc-950/60 rounded-xl border border-zinc-850">
                    <div className="flex justify-between text-[9px] mb-1.5 font-mono">
                      <span>SCALE OUT CPU TRIGGER</span>
                      <span className="text-cyan-400 font-bold">{cpuThreshold}%</span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="90"
                      step="5"
                      value={cpuThreshold}
                      onChange={(e) => setCpuThreshold(parseInt(e.target.value))}
                      className="w-full h-1 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-cyan-500"
                    />
                  </div>

                </div>

                {/* Traffic Spike options */}
                <h3 className="text-xs font-black uppercase tracking-wider mb-3 text-cyan-400">3. Emulate Traffic Spike</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'idle', label: 'Idle' },
                    { id: 'spike', label: 'Growth Run' },
                    { id: 'surge', label: 'Heavy Surge' }
                  ].map(l => (
                    <button
                      key={l.id}
                      onClick={() => applyTrafficLoad(l.id)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all ${
                        trafficLoad === l.id 
                          ? 'bg-cyan-500 text-white font-black scale-102 shadow' 
                          : 'bg-zinc-900 border border-zinc-850 text-zinc-500 hover:text-zinc-350'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>

              </div>

              {/* Action output */}
              <div className="mt-8">
                <button
                  onClick={handleCopySpec}
                  className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 text-white font-black text-xs tracking-wider transition-all"
                >
                  {copiedSpec ? <FaRegCheckCircle size={13} /> : <FaCopy size={12} />}
                  {copiedSpec ? "CONFIG SPEC COPIED!" : "COPY TOPOLOGY SPECS"}
                </button>
              </div>

            </div>

            {/* Right Screen Mock (7 columns) */}
            <div className="lg:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-3xl p-6 flex flex-col justify-between shadow-xl"
                   style={{ background: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
                
                <div>
                  <div className="flex items-center justify-between pb-3 border-b mb-6" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-cyan-500 animate-pulse" />
                      <span className="text-[10px] font-mono uppercase font-bold tracking-widest text-zinc-400">
                        Cloud Load-Balancer Console
                      </span>
                    </div>
                    <span className="text-[8.5px] uppercase font-mono font-black text-cyan-400" >
                      Region: eu-west-1
                    </span>
                  </div>

                  {/* Browser Mock Wrapper */}
                  <div className="w-full border rounded-2xl overflow-hidden relative bg-zinc-950/45 border-zinc-850" style={{ backdropFilter: 'blur(10px)' }}>
                    
                    {/* URL Bar */}
                    <div className="bg-zinc-900 px-4 py-2 border-b border-zinc-850 flex items-center justify-between">
                      <div className="flex gap-1 items-center shrink-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                        <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                      </div>
                      
                      <div className="flex-grow max-w-sm mx-auto h-5 bg-zinc-950 rounded text-[9px] text-zinc-500 flex items-center justify-between px-2 font-mono border border-zinc-850">
                        <span>Load: {currentReqs} reqs/s</span>
                        <span className="text-[8.9px] font-bold text-cyan-400 uppercase">TELEMETRY</span>
                      </div>

                      <div className="w-4" />
                    </div>

                    {/* Page Content Dashboard */}
                    <div className="p-4 space-y-4 text-left h-[260px] overflow-y-auto">
                      
                      {/* Active stats */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-900/60 font-mono">
                          <span className="text-[7.5px] text-zinc-555 block mb-1">AVG CPU UTIL</span>
                          <span className={`text-[12.5px] font-bold ${currentCpu > cpuThreshold ? 'text-red-400' : 'text-cyan-400'}`}>{currentCpu}%</span>
                        </div>
                        <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-900/60 font-mono">
                          <span className="text-[7.5px] text-zinc-555 block mb-1">LATENCY</span>
                          <span className={`text-[12.5px] font-bold ${latency > 100 ? 'text-red-400' : 'text-emerald-400'}`}>{latency}ms</span>
                        </div>
                        <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-900/60 font-mono">
                          <span className="text-[7.5px] text-zinc-555 block mb-1">WORKER NODES</span>
                          <span className="text-[12.5px] font-bold text-white">{activeNodes.length}</span>
                        </div>
                      </div>

                      {/* Spawning Node visual indicators */}
                      <div>
                        <span className="text-[7.5px] uppercase font-mono font-black text-cyan-400 tracking-widest block mb-2">Active Server Worker nodes</span>
                        <div className="flex flex-wrap gap-2">
                          {activeNodes.map((node, i) => (
                            <div key={i} className="px-3 py-1.5 rounded-xl border border-cyan-500/20 bg-cyan-500/5 text-[8.5px] text-cyan-400 font-mono flex items-center gap-1.5 shadow-sm">
                              <span className="w-1.5 h-1.5 rounded bg-cyan-400 animate-pulse shrink-0" />
                              <span>{node}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Logging outposts console */}
                      <div className="p-2.5 rounded-xl border border-zinc-850 bg-zinc-950 font-mono text-[7px]">
                        <span className="text-[6.5px] uppercase font-bold text-zinc-500 block mb-1.5 tracking-wider border-b border-zinc-850 pb-1">Auto-scaler events logs</span>
                        <div className="space-y-1 h-[65px] overflow-y-auto text-zinc-400">
                          {autoscalerLogs.map((log, index) => (
                            <div key={index} className="truncate">
                              <span className="text-zinc-605 mr-1.5">➜</span>
                              <span className={log.includes('SYSTEM:') ? 'text-zinc-500' : 'text-cyan-400'}>{log}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                    </div>

                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── INTERACTIVE TIMELINE MILESTONES ── */}
      <section className="py-24 px-6 border-b" style={{ background: 'var(--bg-card)', borderColor: 'var(--section-divider)' }}>
        <div className="section-container max-w-4xl mx-auto">
          
          <div className="text-center mb-16">
            <p className="eyebrow mb-2">Deployment Sprints</p>
            <h2 className="display-sm mb-4" style={{ color: 'var(--text-primary)' }}>Infrastructure Sprints Setup</h2>
            <p className="subheadline max-w-2xl mx-auto">
              Follow our implementation steps checklist. Click on a stage below to view automated scripts specifications.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-stretch">
            
            {/* Stage selectors (5 columns) */}
            <div className="md:col-span-5 space-y-3">
              {[
                { id: 'terraform', step: '01', title: 'Infra with Terraform', desc: 'Secure AWS/GCP resource parameters states.' },
                { id: 'networks', step: '02', title: 'Isolated VPC Networks', desc: 'Structure public/private subnets and route tables.' },
                { id: 'pipelines', step: '03', title: 'CI/CD Pipeline Builds', desc: 'Configure automated Github Actions compilation checks.' },
                { id: 'caching', step: '04', title: 'Edge CDN Integrations', desc: 'Activate edge caching nodes for static assets.' }
              ].map(s => (
                <button
                  key={s.id}
                  onClick={() => setActiveStage(s.id)}
                  className={`w-full text-left p-3.5 rounded-2xl border flex items-center gap-4 transition-all duration-200 ${
                    activeStage === s.id 
                      ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-455 scale-[1.01]' 
                      : 'border-zinc-800/10 dark:border-zinc-700/10 hover:bg-zinc-800/5'
                  }`}
                >
                  <span className={`text-md font-mono font-black ${activeStage === s.id ? 'text-cyan-405' : 'text-zinc-550'}`}>
                    {s.step}
                  </span>
                  <div>
                    <h4 className="text-xs font-black uppercase text-white leading-none mb-1">{s.title}</h4>
                    <p className="text-[9.5px] text-zinc-450">{s.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Instruction view deck (7 columns) */}
            <div className="md:col-span-7 flex flex-col justify-stretch">
              <div className="w-full h-full border rounded-2xl overflow-hidden bg-zinc-950 border-zinc-850 p-6 font-mono shadow-2xl flex flex-col justify-between">
                <div>
                  <div className="flex justify-between items-center pb-2.5 border-b border-zinc-850 mb-4 text-zinc-500 text-[10px]">
                    <span className="uppercase tracking-widest font-bold">Workspace : platform_infra</span>
                    <span className="text-[9px] text-cyan-400">Step Verified</span>
                  </div>

                  <div className="text-left text-zinc-350 text-[10px] space-y-4">
                    {activeStage === 'terraform' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Terraform configurations:</h4>
                        <p className="leading-relaxed">Write declarative module files referencing database engines, virtual network adapters and security credential roles, locked under remote S3 states locks.</p>
                      </>
                    )}
                    {activeStage === 'networks' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">VPC Configurations:</h4>
                        <p className="leading-relaxed">Structure secure subnets mapping databases inside private clusters (no external IP exposure), routing web payloads only through isolated ALB load balancer channels.</p>
                      </>
                    )}
                    {activeStage === 'pipelines' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Automated CI/CD build scripts:</h4>
                        <p className="leading-relaxed">Setup testing gates, Docker containment builds, ECR registry pushing keys, and zero-downtime rolling deployment check rules on target Kubernetes hosts.</p>
                      </>
                    )}
                    {activeStage === 'caching' && (
                      <>
                        <h4 className="text-white text-xs font-bold font-sans">Reverse caching parameters:</h4>
                        <p className="leading-relaxed">Set up cache invalidation headers, SSL integrations certifications keys, custom rewrite routes, and GeoIP routing paths to manage content distributions globally.</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-zinc-850 flex justify-between items-center text-[8.5px] text-zinc-500 font-sans">
                  <span>Bombay Coding Company DevOps Pipeline</span>
                  <span className="text-cyan-400 font-bold">ACTIVE</span>
                </div>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── BOTTOM BACK CTA ── */}
      <section className="py-20 text-center" style={{ background: 'var(--bg-primary)' }}>
         <Link to="/services" className="text-sm font-semibold transition-colors inline-flex items-center gap-2"
               style={{ color: 'var(--accent)' }}>
            <FaArrowRight size={12} className="transform rotate-180 text-cyan-405" /> Back to All Services
         </Link>
      </section>

    </div>
  );
}
